import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, CaretDown, X } from "@phosphor-icons/react";

/**
 * SearchSelect — a lightweight live-search dropdown (type to filter + pick a suggestion).
 *
 * Controlled by a plain string `value` (so it plugs straight into substring filters). Typing is
 * free-text (updates onChange immediately); picking a suggestion commits that option's value.
 *
 * props:
 *   value, onChange(value)
 *   options: [{ value, label, sub? }]
 *   placeholder, testid, disabled
 */
export const SearchSelect = ({ value = "", onChange, options = [], placeholder = "Search…",
                               testid, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { setQ(value); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    const base = needle
      ? options.filter((o) =>
          (o.label || "").toLowerCase().includes(needle) ||
          (o.sub || "").toLowerCase().includes(needle) ||
          String(o.value || "").toLowerCase().includes(needle))
      : options;
    return base.slice(0, 80);
  }, [q, options]);

  const commit = (opt) => {
    onChange?.(opt.value);
    setQ(opt.value);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { if (open && filtered[hi]) { e.preventDefault(); commit(filtered[hi]); } }
    else if (e.key === "Escape") { setOpen(false); }
  };

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector(`[data-i="${hi}"]`);
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [hi, open]);

  return (
    <div ref={wrapRef} className="ss-wrap" data-testid={testid}>
      <MagnifyingGlass className="ss-ico" size={14} weight="bold" />
      <input
        className="ss-input"
        data-testid={testid ? `${testid}-input` : undefined}
        value={q}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        autoComplete="off"
      />
      {q ? (
        <button type="button" className="ss-clear" title="Clear"
          data-testid={testid ? `${testid}-clear` : undefined}
          onClick={() => { onChange?.(""); setQ(""); setOpen(false); }}>
          <X size={12} weight="bold" />
        </button>
      ) : (
        <CaretDown className="ss-caret" size={12} weight="bold" />
      )}
      {open && !disabled && (
        <div className="ss-menu" ref={listRef} data-testid={testid ? `${testid}-menu` : undefined}>
          {filtered.length === 0 ? (
            <div className="ss-empty">No matches</div>
          ) : (
            filtered.map((o, i) => (
              <button type="button" key={`${o.value}-${i}`} data-i={i}
                className={`ss-opt${i === hi ? " hi" : ""}${o.value === value ? " sel" : ""}`}
                onMouseEnter={() => setHi(i)}
                onClick={() => commit(o)}>
                <span className="ss-opt-label">{o.label ?? o.value}</span>
                {o.sub ? <span className="ss-opt-sub">{o.sub}</span> : null}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
